import { useCallback } from "react";

import Sentry from "@similarweb/sentry-logger";

import type { OnCreation } from "app/modules/project-from-comparison/types";
import { useCreateActivationObjectMutation } from "app/pages/activation/queries/data/useCreateActivationObjectMutation";
import { useSubscribeToAllAlertsMutation } from "app/pages/activation-alerts/queries/alerts/useSubscribeToAllAlertsMutation";

type UseCreateProjectInput = {
    isCreationEnabled: boolean;
    domains: string[];
    country: string;
    projectName: string;
    isWeeklyUpdatesEnabled: boolean;
};

export const useCreateProject = ({
    isCreationEnabled,
    isWeeklyUpdatesEnabled,
    domains,
    country,
    projectName,
}: UseCreateProjectInput) => {
    const { mutateAsync: createProjectAsync, isPending: isProjectCreating } = useCreateActivationObjectMutation();
    const { mutateAsync: subscribeToAlertsAsync } = useSubscribeToAllAlertsMutation({ subscribeToBenchmark: true });

    const onCreate = useCallback(
        async ({ onSuccess, onError, onSettled }: OnCreation) => {
            if (!isCreationEnabled) {
                onSettled?.();

                return;
            }

            const [primaryDomain, ...competitorsDomains] = domains;

            if (!primaryDomain) {
                const error = new Error("Primary domain is required to create a project");
                onError?.(error);
                onSettled?.();
                return;
            }
            try {
                const response = await createProjectAsync({
                    country,
                    competitorsDomains,
                    primaryDomain,
                    name: projectName,
                });

                if (isWeeklyUpdatesEnabled && response.id) {
                    try {
                        void subscribeToAlertsAsync({ activationId: response.id });
                    } catch (alertsError: any) {
                        Sentry.errorTracker({
                            e: alertsError,
                            message:
                                "Error happened while subscribing to Activation Project alerts while created from Website Analysis page",
                            level: "error",
                        });
                    }
                }
                onSuccess?.(response);
            } catch (error: any) {
                onError?.(error);
            } finally {
                onSettled?.();
            }
        },
        [
            country,
            domains,
            isCreationEnabled,
            isWeeklyUpdatesEnabled,
            projectName,
            createProjectAsync,
            subscribeToAlertsAsync,
        ],
    );

    return { onCreate, isProjectCreating };
};
